import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getPartnerWhatsappConversation,
  getPartnerWhatsappTimeline,
} from "../../api/partnerPortalApi";
import PartnerWhatsappTimelineFeed from "../../components/partner/whatsapp/PartnerWhatsappTimelineFeed";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import {
  partnerWhatsappOutcomeLabel,
  partnerWhatsappStatusLabel,
} from "../../constants/partnerWhatsappMonitoringConstants";
import {
  formatWorkflowAge,
  mapPortalAccessStatus,
  mapVendorSyncStatus,
  mapWhatsappStatus,
  normalizePartnerWhatsappConversationDetail,
  normalizePartnerWhatsappTimeline,
} from "../../utils/partnerWhatsappConversationDetailViewModel";
import { formatConversationDate } from "../../utils/partnerWhatsappConversationsViewModel";
import "../../styles/partnerPortalDashboard.css";

const TABS = Object.freeze([
  { id: "timeline", label: "Timeline" },
  { id: "workflow", label: "Workflow" },
  { id: "dealer", label: "Dealer" },
]);

function StatusBadge({ status }) {
  return (
    <span className="partner-whatsapp-badge partner-whatsapp-badge--blue">
      {partnerWhatsappStatusLabel(status)}
    </span>
  );
}

function SummaryCard({ label, value, hint }) {
  return (
    <article className="partner-kpi-card">
      <p className="partner-kpi-card__label">{label}</p>
      <p className="partner-kpi-card__value">{value}</p>
      {hint ? <p className="partner-kpi-card__hint muted">{hint}</p> : null}
    </article>
  );
}

function DetailSkeleton() {
  return (
    <div className="partner-dashboard partner-dashboard-skeleton" aria-busy="true">
      <div className="partner-skeleton-block partner-skeleton-hero" />
      <div className="partner-kpi-grid">
        {[1, 2, 3, 4].map((id) => (
          <div key={id} className="partner-skeleton-block partner-skeleton-kpi" />
        ))}
      </div>
    </div>
  );
}

export default function PartnerWhatsappConversationDetailPage() {
  const { crmLeadId } = useParams();
  const { path } = usePartnerPortalBase();
  const conversationsPath = path("whatsapp/conversations");
  const [activeTab, setActiveTab] = useState("timeline");
  const [detail, setDetail] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setErrorMessage("");
      try {
        const [detailBody, timelineBody] = await Promise.all([
          getPartnerWhatsappConversation(crmLeadId),
          getPartnerWhatsappTimeline(crmLeadId),
        ]);
        if (cancelled) return;
        setDetail(normalizePartnerWhatsappConversationDetail(detailBody));
        setTimeline(normalizePartnerWhatsappTimeline(timelineBody));
      } catch (error) {
        if (!cancelled) {
          setDetail(null);
          setTimeline(null);
          const message = String(error?.message || "");
          if (/permission|denied|not found/i.test(message)) {
            setErrorMessage("This WhatsApp conversation is not available.");
          } else {
            setErrorMessage("We could not load this WhatsApp conversation.");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [crmLeadId]);

  const conversation = detail?.conversation;
  const workflow = detail?.workflow;
  const dealer = detail?.dealer;

  const messageCount = useMemo(() => {
    if (conversation?.messageCount != null) return conversation.messageCount;
    return timeline?.messageCount ?? 0;
  }, [conversation, timeline]);

  if (loading) {
    return <DetailSkeleton />;
  }

  if (errorMessage || !conversation) {
    return (
      <div className="partner-panel partner-whatsapp-page">
        <p className="admin-data-health-error">
          {errorMessage || "We could not load this WhatsApp conversation."}
        </p>
        <Link to={conversationsPath}>Back to conversations</Link>
      </div>
    );
  }

  return (
    <div className="partner-dashboard partner-whatsapp-page">
      <header className="partner-dashboard-hero">
        <div>
          <p className="partner-dashboard-hero__eyebrow">
            <Link to={conversationsPath}>WhatsApp Conversations</Link>
          </p>
          <h2>{conversation.leadRef || conversation.crmLeadId}</h2>
          <p className="partner-dashboard-hero__intro">
            {conversation.dealerName || "Dealer"} · {conversation.vehicleSummary || "Vehicle"}
          </p>
        </div>
        <div className="partner-whatsapp-detail__header-meta">
          <StatusBadge status={conversation.brokerStatus} />
          <Link className="partner-dashboard-btn partner-dashboard-btn--ghost" to={conversationsPath}>
            Back to list
          </Link>
        </div>
      </header>

      <section className="partner-kpi-grid partner-whatsapp-detail__summary">
        <SummaryCard
          label="Current workflow"
          value={partnerWhatsappOutcomeLabel(workflow?.currentStep || conversation.outcome)}
          hint={workflow?.recommendedAction || null}
        />
        <SummaryCard
          label="Last activity"
          value={formatConversationDate(conversation.lastActivityAt)}
        />
        <SummaryCard
          label="Outcome"
          value={partnerWhatsappOutcomeLabel(conversation.outcome)}
        />
        <SummaryCard
          label="Manual outreach"
          value={conversation.manualOutreachRequired ? "Required" : "Not required"}
          hint={conversation.manualOutreachReason || null}
        />
        <SummaryCard label="Messages" value={String(messageCount)} />
      </section>

      <nav className="partner-whatsapp-detail__tabs" aria-label="Conversation sections">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={
              activeTab === tab.id
                ? "partner-whatsapp-detail__tab partner-whatsapp-detail__tab--active"
                : "partner-whatsapp-detail__tab"
            }
            aria-current={activeTab === tab.id ? "page" : undefined}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <section className="partner-panel">
        {activeTab === "timeline" ? (
          <>
            {timeline?.degraded ? (
              <p className="partner-whatsapp-detail__degraded muted">
                Some timeline data is temporarily unavailable. Showing available events only.
              </p>
            ) : null}
            <PartnerWhatsappTimelineFeed items={timeline?.items || []} />
          </>
        ) : null}

        {activeTab === "workflow" ? (
          <dl className="partner-whatsapp-detail__facts">
            <div>
              <dt>Current step</dt>
              <dd>{partnerWhatsappOutcomeLabel(workflow?.currentStep || conversation.outcome)}</dd>
            </div>
            <div>
              <dt>Age in current status</dt>
              <dd>{formatWorkflowAge(workflow?.ageInCurrentStatusMinutes)}</dd>
            </div>
            <div>
              <dt>Stuck</dt>
              <dd>{workflow?.isStuck || conversation.isStuck ? "Yes" : "No"}</dd>
            </div>
            {workflow?.stuckReason || conversation.stuckReason ? (
              <div>
                <dt>Stuck reason</dt>
                <dd>{workflow?.stuckReason || conversation.stuckReason}</dd>
              </div>
            ) : null}
            {workflow?.recommendedAction ? (
              <div>
                <dt>Recommended action</dt>
                <dd>{workflow.recommendedAction}</dd>
              </div>
            ) : null}
            {workflow?.failureReason ? (
              <div>
                <dt>Failure reason</dt>
                <dd>{workflow.failureReason}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}

        {activeTab === "dealer" ? (
          <dl className="partner-whatsapp-detail__facts">
            <div>
              <dt>Dealer</dt>
              <dd>{dealer?.dealerName || conversation.dealerName || "Dealer"}</dd>
            </div>
            <div>
              <dt>WhatsApp status</dt>
              <dd>{mapWhatsappStatus(dealer?.whatsappStatus)}</dd>
            </div>
            <div>
              <dt>Vendor sync</dt>
              <dd>{mapVendorSyncStatus(dealer?.vendorSyncStatus)}</dd>
            </div>
            <div>
              <dt>Portal access</dt>
              <dd>{mapPortalAccessStatus(dealer?.portalAccessStatus)}</dd>
            </div>
            <div>
              <dt>Manual outreach flag</dt>
              <dd>{conversation.manualOutreachRequired ? "Required" : "Not required"}</dd>
            </div>
          </dl>
        ) : null}
      </section>
    </div>
  );
}
